import { useMemo } from 'react';
import {
  PHASES_OF_PLAY,
  PHASE_OF_PLAY_META,
  type PhaseOfPlay,
} from '@4cm/core/phaseOfPlay';

export interface PhaseCoverageStripProps {
  /** Drills currently sequenced. We re-derive coverage from durations + phases on every change so the bar is always live. */
  drills: ReadonlyArray<{ phase?: PhaseOfPlay | null; duration_mins?: number | null }>;
  /** Optional override label — defaults to "Phase mix". */
  label?: string;
}

/**
 * Phase coverage strip — mirrors `apps/web2/src/pages/Calendar.tsx`'s
 * `calendar-phase-mix` row but operates on a single session's drill list
 * rather than a week of sessions. Updates live as drills are added,
 * removed, or re-ordered. Hidden when no drills carry phase data.
 */
export function PhaseCoverageStrip({ drills, label = 'Phase mix' }: PhaseCoverageStripProps) {
  const totals = useMemo(() => {
    const t: Record<PhaseOfPlay, number> = {
      attacking: 0, defending: 0, transitions: 0, individual: 0,
    };
    for (const d of drills) {
      const phase = d.phase;
      if (!phase) continue;
      const mins = Number(d.duration_mins ?? 0);
      if (mins <= 0) continue;
      t[phase] += mins;
    }
    return t;
  }, [drills]);

  const total = PHASES_OF_PLAY.reduce((sum, p) => sum + totals[p], 0);
  if (total <= 0) {
    return (
      <div className="session-canvas__phase-empty" data-testid="phase-coverage-empty">
        Add a drill to see phase mix.
      </div>
    );
  }

  // Sentence-style label for screen readers — mirrors the visual bar.
  // e.g. "Phase coverage: Attacking 30%, Defending 25%, Transitions 30%, Individual 15%"
  const segmentSummaries = PHASES_OF_PLAY
    .map((phase) => {
      const value = totals[phase];
      if (value <= 0) return null;
      const pct = Math.round((value / total) * 100);
      return `${PHASE_OF_PLAY_META[phase].label} ${pct}%`;
    })
    .filter((s): s is string => s !== null);
  const barAriaLabel = `Phase coverage: ${segmentSummaries.join(', ')}`;

  return (
    <section className="session-canvas__phase-mix" aria-label="Session phase coverage">
      <div className="session-canvas__phase-label">{label}</div>
      <div
        className="session-canvas__phase-bar"
        role="img"
        aria-label={barAriaLabel}
        data-testid="phase-coverage-bar"
      >
        {PHASES_OF_PLAY.map((phase) => {
          const value = totals[phase];
          if (value <= 0) return null;
          const pct = Math.round((value / total) * 100);
          return (
            <span
              key={phase}
              className="session-canvas__phase-seg"
              data-phase={phase}
              data-pct={pct}
              style={{ flex: pct, background: PHASE_OF_PLAY_META[phase].colorHex }}
              title={`${PHASE_OF_PLAY_META[phase].label} ${pct}%`}
            >
              <span className="session-canvas__phase-pct">{pct}%</span>
            </span>
          );
        })}
      </div>
      <div className="session-canvas__phase-legend">
        {PHASES_OF_PLAY.map((phase) => {
          const value = totals[phase];
          if (value <= 0) return null;
          return (
            <span key={phase} className="session-canvas__phase-legend-item">
              <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
              {PHASE_OF_PLAY_META[phase].label}
            </span>
          );
        })}
      </div>
    </section>
  );
}
